import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { findings as findingsApi, users as usersApi } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Search, Edit3, Share2, Copy, Trash2, Check,
  Clock, ListTodo, X as XIcon, Camera, Link2, UserPlus, MessageSquare,
  LayoutList, LayoutGrid, Send,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import SmsDialog from "../components/SmsDialog";
import GroupSmsDialog from "../components/GroupSmsDialog";
import { localizedCategory } from "../lib/i18nContent";

const SEVERITY = ["Alta", "Media", "Baja"];
const STATUSES = ["to_do", "in_progress", "done", "cannot_do"];

const STATUS_VISUAL = {
  to_do: { icon: ListTodo, classes: "bg-stone-100 text-stone-700 ring-stone-400/20" },
  in_progress: { icon: Clock, classes: "bg-amber-50 text-amber-700 ring-amber-600/20" },
  done: { icon: Check, classes: "bg-emerald-50 text-emerald-700 ring-emerald-600/20" },
  cannot_do: { icon: XIcon, classes: "bg-red-50 text-red-700 ring-red-600/20" },
};

function severityColor(s) {
  return s === "Alta"
    ? "bg-red-50 text-red-700 ring-red-600/20"
    : s === "Media"
    ? "bg-amber-50 text-amber-700 ring-amber-600/20"
    : "bg-stone-100 text-stone-700 ring-stone-400/20";
}

export default function FindingsPage() {
  const { t, i18n } = useTranslation();
  const { user, viewAllStores, can } = useApp();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [sharing, setSharing] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [smsTarget, setSmsTarget] = useState(null);
  const [groupByStore, setGroupByStore] = useState(false);
  const [groupSmsStore, setGroupSmsStore] = useState(null); // {name, findings}
  const [assignable, setAssignable] = useState([]);
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");
  const rowRefs = useRef({});
  const [flashId, setFlashId] = useState(null);
  const [newAuditorOpen, setNewAuditorOpen] = useState(false);
  const [newAuditor, setNewAuditor] = useState({
    name: "",
    email: "",
    password: "",
    role: "auditor",
  });

  const load = () =>
    findingsApi
      .list({ view_all: viewAllStores })
      .then(setItems)
      .catch(console.error);

  const loadAssignable = () =>
    usersApi.assignable().then(setAssignable).catch(console.error);

  useEffect(() => {
    load();
    loadAssignable();
  }, [viewAllStores]);

  // Auto-refresh every 15 seconds (so updates from public-finding page show up live)
  useEffect(() => {
    const id = setInterval(() => {
      load();
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAllStores]);

  // Highlight+scroll when arriving via bell dropdown (?highlight=<finding_id>)
  useEffect(() => {
    if (!highlightId || items.length === 0) return;
    const target = items.find((f) => f.id === highlightId);
    if (!target) return;
    // If the finding is filtered out, clear filters first
    if (status !== "all" && target.status !== status) setStatus("all");
    if (q.trim()) setQ("");
    // Scroll + flash animation
    setTimeout(() => {
      const el = rowRefs.current[highlightId];
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      setFlashId(highlightId);
      setTimeout(() => setFlashId(null), 2500);
    }, 120);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightId, items.length]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    for (const s of STATUSES) c[s] = 0;
    for (const f of items) {
      if (c[f.status] !== undefined) c[f.status] += 1;
    }
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    let arr = items;
    if (status !== "all") arr = arr.filter((f) => f.status === status);
    if (q.trim()) {
      const v = q.toLowerCase();
      arr = arr.filter(
        (f) =>
          f.description.toLowerCase().includes(v) ||
          (f.store_name || "").toLowerCase().includes(v) ||
          (f.category || "").toLowerCase().includes(v),
      );
    }
    return arr;
  }, [items, q, status]);

  // Group filtered findings by store (only used when groupByStore = true)
  const grouped = useMemo(() => {
    if (!groupByStore) return [];
    const map = new Map();
    for (const f of filtered) {
      const key = f.store_name || "—";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }
    // Sort stores alphabetically, and each store's findings by status priority
    const priority = { to_do: 0, in_progress: 1, cannot_do: 2, done: 3 };
    const arr = Array.from(map.entries()).map(([store_name, list]) => ({
      store_name,
      findings: list.slice().sort((a, b) => (priority[a.status] ?? 99) - (priority[b.status] ?? 99)),
      counts: {
        to_do: list.filter((f) => f.status === "to_do").length,
        in_progress: list.filter((f) => f.status === "in_progress").length,
        done: list.filter((f) => f.status === "done").length,
        cannot_do: list.filter((f) => f.status === "cannot_do").length,
        total: list.length,
      },
    }));
    arr.sort((a, b) => a.store_name.localeCompare(b.store_name));
    return arr;
  }, [filtered, groupByStore]);

  const openEdit = (f) => {
    setEditing(f);
    setForm({
      responsible: f.responsible || "",
      responsible_id: f.responsible_id || "",
      commitment_date: f.commitment_date || "",
      severity: f.severity || "Media",
      status: f.status || "to_do",
      follow_up_notes: f.follow_up_notes || "",
    });
  };

  const onResponsibleChange = (val) => {
    if (val === "__none__") {
      setForm({ ...form, responsible: "", responsible_id: "" });
      return;
    }
    const u = assignable.find((x) => x.id === val);
    if (!u) return;
    setForm({ ...form, responsible: u.name, responsible_id: u.id });
  };

  const openNewAuditor = () => {
    setNewAuditor({ name: "", email: "", password: "", role: "auditor" });
    setNewAuditorOpen(true);
  };

  const submitNewAuditor = async () => {
    if (!newAuditor.name.trim() || !newAuditor.email.trim() || !newAuditor.password.trim()) {
      toast.error(t("checklist_page.required"));
      return;
    }
    try {
      const u = await usersApi.quickCreate(newAuditor);
      toast.success(t("finding_assign.created_assigned"));
      setNewAuditorOpen(false);
      // refresh list and auto-select the new user
      await loadAssignable();
      setForm((f) => ({ ...f, responsible: u.name, responsible_id: u.id }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const save = async () => {
    await findingsApi.patch(editing.id, form);
    if (form.responsible_id && form.responsible_id !== editing.responsible_id) {
      toast.success(t("finding_assign.assigned", { name: form.responsible }));
    } else {
      toast.success(t("findings.updated"));
    }
    setEditing(null);
    load();
  };

  const openShare = async (f) => {
    setSharing(f);
    setShareUrl("");
    try {
      const r = await findingsApi.share(f.id);
      const url = `${window.location.origin}${r.path}`;
      setShareUrl(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
      setSharing(null);
    }
  };

  const copyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("finding_share.copied"));
    } catch {
      toast.error(t("finding_share.copy_failed"));
    }
  };

  const revokeShare = async () => {
    if (!sharing) return;
    if (!window.confirm(t("finding_share.confirm_revoke"))) return;
    try {
      await findingsApi.revokeShare(sharing.id);
      toast.success(t("finding_share.revoked"));
      setSharing(null);
      setShareUrl("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const remove = async (f) => {
    if (!window.confirm(t("findings.confirm_delete"))) return;
    await findingsApi.remove(f.id);
    load();
  };

  const canEdit = can("edit_findings");
  const canShare = can("share_finding");
  const canDelete = can("delete_finding");
  const canSms = can("send_sms");

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] mx-auto" data-testid="findings-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="canales-overline">{t("findings.title")}</div>
          <h1 className="font-display text-4xl md:text-5xl mt-1">{t("findings.page_subtitle")}</h1>
          <p className="text-stone-600 mt-2 text-sm max-w-xl">{t("findings.subtitle")}</p>
        </div>
      </div>

      {/* Status pills */}
      <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="findings-status-pills">
        <button
          onClick={() => setStatus("all")}
          className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
            status === "all"
              ? "bg-[#0B1220] text-white"
              : "bg-white border border-stone-200 text-stone-700 hover:border-stone-300"
          }`}
          data-testid="status-pill-all"
        >
          {t("findings.all_status")} <span className="opacity-60 ml-1">{counts.all}</span>
        </button>
        {STATUSES.map((s) => {
          const v = STATUS_VISUAL[s];
          const Icon = v.icon;
          return (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1.5 ${
                status === s
                  ? "bg-[#0B1220] text-white"
                  : "bg-white border border-stone-200 text-stone-700 hover:border-stone-300"
              }`}
              data-testid={`status-pill-${s}`}
            >
              <Icon size={12} />
              {t(`finding_status.${s}`)}
              <span className="opacity-60 ml-1">{counts[s] || 0}</span>
            </button>
          );
        })}
      </div>

      <Card className="canales-card mt-4 p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[240px] px-2">
            <Search size={16} className="text-stone-500" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("findings.search_placeholder")}
              className="border-0 focus-visible:ring-0 px-0"
              data-testid="findings-search"
            />
          </div>
          {/* View mode toggle: table vs group-by-store */}
          <div className="inline-flex rounded-full border border-stone-200 p-0.5 bg-white" data-testid="findings-view-toggle">
            <button
              type="button"
              onClick={() => setGroupByStore(false)}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                !groupByStore
                  ? "bg-[#0B1220] text-white"
                  : "text-stone-600 hover:text-stone-900"
              }`}
              data-testid="findings-view-table"
            >
              <LayoutList size={12} /> {t("findings.view_table", "Tabla")}
            </button>
            <button
              type="button"
              onClick={() => setGroupByStore(true)}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                groupByStore
                  ? "bg-[#0B1220] text-white"
                  : "text-stone-600 hover:text-stone-900"
              }`}
              data-testid="findings-view-group"
            >
              <LayoutGrid size={12} /> {t("findings.view_group", "Agrupar por tienda")}
            </button>
          </div>
          <span className="text-xs text-stone-500">
            🔄 {t("findings.auto_refresh", "Auto-refresh 15s")}
          </span>
        </div>

        {!groupByStore && (
        <div className="mt-3 overflow-x-auto" data-testid="findings-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("findings.col_store")}</TableHead>
                <TableHead>{t("findings.col_category")}</TableHead>
                <TableHead>{t("findings.col_description")}</TableHead>
                <TableHead>{t("findings.col_responsible")}</TableHead>
                <TableHead>{t("findings.col_commitment")}</TableHead>
                <TableHead>{t("findings.col_severity")}</TableHead>
                <TableHead>{t("findings.col_status")}</TableHead>
                <TableHead>{t("findings.col_photos", "Fotos")}</TableHead>
                <TableHead className="text-right">{t("findings.col_action")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12 text-stone-500">
                    {t("findings.no_findings")}
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((f) => {
                const v = STATUS_VISUAL[f.status] || STATUS_VISUAL.to_do;
                const Icon = v.icon;
                const photos = f.photos && f.photos.length > 0 ? f.photos : (f.photo_url ? [{ url: f.photo_url }] : []);
                return (
                  <TableRow
                    key={f.id}
                    data-testid={`finding-row-${f.id}`}
                    ref={(el) => { if (el) rowRefs.current[f.id] = el; }}
                    className={flashId === f.id ? "bg-amber-100 ring-2 ring-amber-400 transition-all" : ""}
                  >
                    <TableCell className="font-medium">{f.store_name}</TableCell>
                    <TableCell className="text-stone-600">{localizedCategory(f.category, i18n.language)}</TableCell>
                    <TableCell className="max-w-md">
                      <div className="truncate" title={f.description}>{f.description}</div>
                      {f.follow_up_notes && (
                        <div className="text-xs text-stone-500 mt-1 truncate" title={f.follow_up_notes}>
                          📝 {f.follow_up_notes}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-stone-600">{f.responsible || "—"}</TableCell>
                    <TableCell className="text-stone-600">{f.commitment_date || "—"}</TableCell>
                    <TableCell>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${severityColor(f.severity)}`}>
                        {t(`severity.${f.severity}`, f.severity)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${v.classes}`}>
                        <Icon size={11} />
                        {t(`finding_status.${f.status}`, f.status)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {photos.length > 0 ? (
                        <div className="flex -space-x-1.5">
                          {photos.slice(0, 3).map((p, i) => (
                            <a
                              key={i}
                              href={p.url}
                              target="_blank"
                              rel="noreferrer"
                              className="w-8 h-8 rounded-md border-2 border-white overflow-hidden bg-stone-100 flex items-center justify-center hover:scale-110 transition-transform"
                              title={p.original_filename || "evidencia"}
                            >
                              <img src={p.url} alt="evidencia" className="w-full h-full object-cover" />
                            </a>
                          ))}
                          {photos.length > 3 && (
                            <span className="w-8 h-8 rounded-md border-2 border-white bg-stone-100 grid place-items-center text-[10px] font-semibold text-stone-600">
                              +{photos.length - 3}
                            </span>
                          )}
                        </div>
                      ) : (
                        <Camera size={14} className="text-stone-300" />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {f.share_token && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20" title={t("finding_share.link_active")}>
                            <Link2 size={10} />
                          </span>
                        )}
                        {canShare && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openShare(f)}
                            data-testid={`share-finding-${f.id}`}
                            title={t("finding_share.share_button")}
                          >
                            <Share2 size={14} />
                          </Button>
                        )}
                        {canEdit && (
                          <Button size="sm" variant="outline" onClick={() => openEdit(f)} data-testid={`edit-finding-${f.id}`} className="gap-1.5 text-xs">
                            <Edit3 size={13} /> {t("finding_assign.edit_dialog_title")}
                          </Button>
                        )}
                        {canSms && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSmsTarget(f)}
                            className="gap-1.5 text-xs text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300"
                            data-testid={`sms-finding-${f.id}`}
                            title={t("sms.title")}
                          >
                            <MessageSquare size={13} /> {t("sms.button_label")}
                          </Button>
                        )}
                        {canDelete && (
                          <Button size="sm" variant="outline" onClick={() => remove(f)} data-testid={`delete-finding-${f.id}`} className="text-stone-500 hover:text-red-600">
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        )}

        {groupByStore && (
          <div className="mt-3 space-y-3" data-testid="findings-group-view">
            {grouped.length === 0 && (
              <div className="text-center py-12 text-stone-500 text-sm">
                {t("findings.no_findings")}
              </div>
            )}
            {grouped.map((g) => (
              <div
                key={g.store_name}
                className="rounded-xl border border-stone-200 bg-white overflow-hidden"
                data-testid={`findings-group-${g.store_name}`}
              >
                {/* Store header row */}
                <div className="flex items-center justify-between gap-3 flex-wrap bg-stone-50 border-b border-stone-200 px-4 py-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-stone-800 text-sm truncate">
                      {g.store_name}
                    </div>
                    <div className="text-[11px] text-stone-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>{t("findings.group_total", "{{n}} hallazgos", { n: g.counts.total })}</span>
                      {g.counts.to_do > 0 && (
                        <span className="inline-flex items-center gap-1 text-stone-600">
                          <ListTodo size={10} /> {g.counts.to_do}
                        </span>
                      )}
                      {g.counts.in_progress > 0 && (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <Clock size={10} /> {g.counts.in_progress}
                        </span>
                      )}
                      {g.counts.done > 0 && (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <Check size={10} /> {g.counts.done}
                        </span>
                      )}
                      {g.counts.cannot_do > 0 && (
                        <span className="inline-flex items-center gap-1 text-red-700">
                          <XIcon size={10} /> {g.counts.cannot_do}
                        </span>
                      )}
                    </div>
                  </div>
                  {canSms && (
                    <Button
                      size="sm"
                      onClick={() => setGroupSmsStore({ name: g.store_name, findings: g.findings })}
                      className="brand-button gap-1.5 text-xs"
                      data-testid={`send-group-sms-${g.store_name}`}
                    >
                      <Send size={13} /> {t("findings.send_checklist", "Enviar checklist SMS")}
                    </Button>
                  )}
                </div>

                {/* Store rows (compact) */}
                <ul className="divide-y">
                  {g.findings.map((f) => {
                    const v = STATUS_VISUAL[f.status] || STATUS_VISUAL.to_do;
                    const Icon = v.icon;
                    return (
                      <li
                        key={f.id}
                        ref={(el) => { if (el) rowRefs.current[f.id] = el; }}
                        className={`px-4 py-2.5 flex items-start gap-3 hover:bg-stone-50 ${
                          flashId === f.id ? "bg-amber-100 ring-2 ring-amber-400" : ""
                        }`}
                        data-testid={`group-finding-row-${f.id}`}
                      >
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-medium ring-1 ring-inset ${v.classes} shrink-0`}>
                          <Icon size={10} />
                          {t(`finding_status.${f.status}`, f.status)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] text-stone-800 truncate" title={f.description}>
                            <span className="font-medium text-stone-600">{localizedCategory(f.category, i18n.language)}:</span>{" "}
                            {f.description}
                          </div>
                          <div className="text-[11px] text-stone-500 flex items-center gap-2 flex-wrap mt-0.5">
                            {f.responsible && <span>👤 {f.responsible}</span>}
                            {f.commitment_date && <span>📅 {f.commitment_date}</span>}
                            <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium ring-1 ring-inset ${severityColor(f.severity)}`}>
                              {t(`severity.${f.severity}`, f.severity)}
                            </span>
                          </div>
                        </div>
                        {canEdit && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEdit(f)}
                            className="shrink-0 gap-1 text-xs h-7 px-2"
                            data-testid={`group-edit-finding-${f.id}`}
                          >
                            <Edit3 size={11} />
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="edit-finding-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wide">
              {t("finding_assign.edit_dialog_title")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-stone-600 bg-stone-50 p-3 rounded">
              <div className="font-semibold">{editing?.store_name} · {localizedCategory(editing?.category, i18n.language)}</div>
              <div className="text-stone-700 mt-1">{editing?.description}</div>
            </div>
            <div>
              <label className="canales-overline">{t("finding_assign.responsible_label")}</label>
              <div className="flex items-center gap-2 mt-1">
                <Select
                  value={form.responsible_id || "__none__"}
                  onValueChange={onResponsibleChange}
                >
                  <SelectTrigger className="flex-1" data-testid="edit-finding-responsible-select">
                    <SelectValue placeholder={t("finding_assign.select_placeholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t("finding_assign.none_option")}</SelectItem>
                    {assignable.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} <span className="text-xs text-stone-400 ml-1">· {u.role}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={openNewAuditor}
                  className="gap-1.5 shrink-0"
                  data-testid="finding-new-auditor-button"
                >
                  <UserPlus size={14} />
                  {t("finding_assign.add_auditor")}
                </Button>
              </div>
              {form.responsible_id && (
                <p className="text-[11px] text-stone-500 mt-1">
                  📨 {t("finding_assign.notify_on_assign")}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="canales-overline">{t("findings.col_commitment")}</label>
                <Input
                  type="date"
                  value={form.commitment_date || ""}
                  onChange={(e) => setForm({ ...form, commitment_date: e.target.value })}
                  className="mt-1"
                  data-testid="edit-finding-date"
                />
              </div>
              <div>
                <label className="canales-overline">{t("findings.col_severity")}</label>
                <Select
                  value={form.severity}
                  onValueChange={(v) => setForm({ ...form, severity: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="edit-finding-severity">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITY.map((s) => (
                      <SelectItem key={s} value={s}>
                        {t(`severity.${s}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="canales-overline">{t("findings.col_status")}</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1" data-testid="edit-finding-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {t(`finding_status.${s}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="canales-overline">{t("findings.notes_label")}</label>
              <Textarea
                value={form.follow_up_notes || ""}
                onChange={(e) => setForm({ ...form, follow_up_notes: e.target.value })}
                className="mt-1"
                data-testid="edit-finding-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="brand-button" data-testid="save-finding-button">
              {t("findings.save_changes")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New auditor (quick) */}
      <Dialog open={newAuditorOpen} onOpenChange={setNewAuditorOpen}>
        <DialogContent className="max-w-md" data-testid="new-auditor-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={16} className="text-[#1E3A8A]" />
              {t("finding_assign.new_dialog_title")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="canales-overline">{t("finding_assign.new_name")}</label>
              <Input
                value={newAuditor.name}
                onChange={(e) => setNewAuditor({ ...newAuditor, name: e.target.value })}
                className="mt-1"
                placeholder="Lupita Canales"
                data-testid="new-auditor-name"
                autoFocus
              />
            </div>
            <div>
              <label className="canales-overline">{t("finding_assign.new_email")}</label>
              <Input
                type="email"
                value={newAuditor.email}
                onChange={(e) => setNewAuditor({ ...newAuditor, email: e.target.value.toLowerCase().trim() })}
                className="mt-1"
                placeholder="lupita@canales.com"
                data-testid="new-auditor-email"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="canales-overline">{t("finding_assign.new_password")}</label>
                <Input
                  value={newAuditor.password}
                  onChange={(e) => setNewAuditor({ ...newAuditor, password: e.target.value })}
                  className="mt-1"
                  placeholder="••••••"
                  data-testid="new-auditor-password"
                />
              </div>
              <div>
                <label className="canales-overline">{t("finding_assign.new_role")}</label>
                <Select
                  value={newAuditor.role}
                  onValueChange={(v) => setNewAuditor({ ...newAuditor, role: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="new-auditor-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auditor">{t("finding_assign.role_auditor")}</SelectItem>
                    <SelectItem value="gerente">{t("finding_assign.role_gerente")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewAuditorOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={submitNewAuditor} className="brand-button gap-1.5" data-testid="new-auditor-save">
              <UserPlus size={14} />
              {t("finding_assign.new_save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      <Dialog open={!!sharing} onOpenChange={(v) => !v && setSharing(null)}>
        <DialogContent className="max-w-lg" data-testid="finding-share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 size={18} className="text-[#1E3A8A]" />
              {t("finding_share.title")}
            </DialogTitle>
            <p className="text-xs text-stone-500 mt-1">{t("finding_share.subtitle")}</p>
          </DialogHeader>
          <div className="text-sm text-stone-600 bg-stone-50 p-3 rounded">
            <div className="font-semibold">{sharing?.store_name} · {localizedCategory(sharing?.category, i18n.language)}</div>
            <div className="text-stone-700 mt-1 line-clamp-2">{sharing?.description}</div>
          </div>
          {shareUrl ? (
            <div className="space-y-2 mt-2">
              <div className="flex items-center gap-2">
                <Input value={shareUrl} readOnly className="text-xs" data-testid="share-url-input" />
                <Button size="sm" onClick={copyShare} className="brand-button gap-1.5 shrink-0" data-testid="share-copy-button">
                  <Copy size={14} /> {t("finding_share.copy")}
                </Button>
              </div>
              <p className="text-[11px] text-stone-500">
                {t("finding_share.subtitle")}
              </p>
            </div>
          ) : (
            <p className="text-sm text-stone-400 py-3">{t("common.loading")}</p>
          )}
          <DialogFooter className="gap-2">
            {shareUrl && (
              <Button variant="outline" onClick={revokeShare} className="text-red-600 hover:text-red-700" data-testid="share-revoke-button">
                {t("finding_share.revoke")}
              </Button>
            )}
            <Button variant="outline" onClick={() => setSharing(null)}>{t("common.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SMS dialog */}
      <SmsDialog
        open={!!smsTarget}
        onOpenChange={(v) => !v && setSmsTarget(null)}
        finding={smsTarget}
        defaultLink={smsTarget?.share_token ? `${window.location.origin}/api/share/f/${smsTarget.share_token}` : ""}
      />

      {/* Group checklist SMS dialog */}
      <GroupSmsDialog
        open={!!groupSmsStore}
        onOpenChange={(v) => !v && setGroupSmsStore(null)}
        storeName={groupSmsStore?.name || ""}
        findings={groupSmsStore?.findings || []}
      />
    </div>
  );
}
